'use client';

import { c as _c } from "react/compiler-runtime";
import { Sparkles } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { Pill, PreviewWindow } from '@/app/onboarding/_components/workspace-setup/previews/preview-frame';
import { useTicker } from '@/app/onboarding/_components/workspace-setup/previews/use-ticker';
const QUESTION = 'Which deals need me this week?';
const ANSWERS = [{
  deal: '123 Main St',
  task: 'Inspection response',
  badge: 'Due Thu',
  tone: 'amber'
}, {
  deal: '88 Oak Ave',
  task: 'Seller signature missing',
  badge: '1 left',
  tone: 'blue'
}, {
  deal: 'Unit 2B',
  task: 'Lease renewal',
  badge: '9 days',
  tone: 'gray'
}] as const;

// In TICK_MS ticks: type, "think", hold the answer.
const TICK_MS = 55;
const THINK_TICKS = 10;
const HOLD_TICKS = 70;
const ANSWER_AT = QUESTION.length + THINK_TICKS;

/** A question typed to Clai, answered from deals. */
export function AskClaiPreview() {
  const $ = _c(18);
  const tick = useTicker(ANSWER_AT + HOLD_TICKS, TICK_MS);
  let t0;
  if ($[0] !== tick) {
    t0 = QUESTION.slice(0, tick);
    $[0] = tick;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const typed = t0;
  const isTyping = tick < QUESTION.length;
  const isAnswered = tick >= ANSWER_AT;
  let t1;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <Sparkles className="text-primary size-4 shrink-0" data-sentry-element="Sparkles" data-sentry-source-file="ask-clai-preview.tsx" />;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== typed) {
    t2 = typed || <span className="text-muted-foreground">Ask Clai anything…</span>;
    $[3] = typed;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isTyping) {
    t3 = isTyping ? <span className="bg-muted-foreground ml-px inline-block h-4 w-px animate-pulse align-middle" /> : null;
    $[5] = isTyping;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t2 || $[8] !== t3) {
    t4 = <div className="border-border flex items-center gap-2 rounded-xl border px-3 py-2.5">{t1}<span className="text-foreground truncate text-sm">{t2}{t3}</span></div>;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== isAnswered || $[11] !== isTyping) {
    t5 = isAnswered ? <motion.div key="answer" initial={{
      opacity: 0,
      y: 8
    }} animate={{
      opacity: 1,
      y: 0
    }} exit={{
      opacity: 0
    }}><p className="text-foreground mb-2 text-sm font-medium">{ANSWERS.length} deals need you this week</p><ul className="space-y-2">{ANSWERS.map(_temp)}</ul></motion.div> : isTyping ? null : <motion.p key="thinking" className="animate-clai-shimmer w-fit text-sm font-medium" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }}>Checking your deals…</motion.p>;
    $[10] = isAnswered;
    $[11] = isTyping;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t5) {
    t6 = <div className="mt-4 min-h-40"><AnimatePresence mode="wait" initial={false} data-sentry-element="AnimatePresence" data-sentry-source-file="ask-clai-preview.tsx">{t5}</AnimatePresence></div>;
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t4 || $[16] !== t6) {
    t7 = <PreviewWindow title="Clai AI" data-sentry-element="PreviewWindow" data-sentry-component="AskClaiPreview" data-sentry-source-file="ask-clai-preview.tsx">{t4}{t6}</PreviewWindow>;
    $[15] = t4;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  return t7;
}
function _temp(answer, i) {
  return <motion.li key={answer.deal} className="bg-muted/60 flex items-center justify-between gap-2 rounded-lg px-3 py-2" initial={{
    opacity: 0,
    x: -10
  }} animate={{
    opacity: 1,
    x: 0
  }} transition={{
    delay: 0.12 + i * 0.15
  }}><span className="min-w-0 text-left"><span className="text-foreground block truncate text-xs font-medium">{answer.deal}</span><span className="text-muted-foreground block truncate text-xs">{answer.task}</span></span><Pill tone={answer.tone}>{answer.badge}</Pill></motion.li>;
}